import { DIST_EPSILON } from "../core";
import { CurveCurveRangeIntersection, intersectLine2Line2 } from "../intersectLine2Line2";
import { CurveCurvePointIntersection } from "../intersectLine2Line2";
import { type PolyLine2 } from "../polyLine2Types";
import { vec2Add, vec2Cross, vec2Dist, vec2Dot, vec2Sub } from "../vec2";
import type { Point, DCEL } from "./dcel";
import { dcelFromPolygons, oppositeHalfEdgeIndex } from "./dcelFromPolygons";
import { setOpByPredicatePolygons } from "./setOps";
import { polygonToFaceLoops } from "./trimFaceWithPlane";
import { vec2Normal, vec2Tangent } from "./vec2";

type OffsetDistance = number | number[] | number[][];

/**
 * Offsets a collection of PolyLine2 loops and returns a collection of PolyLine2 groups. The input
 * should start with a CW loop and a series of internal CCW loops. The output will be similarly structured.
 *
 * See https://mcmains.me.berkeley.edu/pubs/DAC05OffsetPolygon.pdf for a description of the algorithm
 *
 * @param polyline The input polyline to offset.
 * @param distance The distance(s) to offset the polyline. Can be a single number, an array of numbers (per polyline), or an array of arrays (per segment per polyline).
 * @returns A list of PolyLine2 objects representing the offset geometry.
 */
export function offsetClosedPolyLine2(polyline: PolyLine2[], distance: OffsetDistance): PolyLine2[][] {
  if (Array.isArray(distance)) {
    if (Array.isArray(distance[0])) {
      if ((distance as number[][]).length !== polyline.length) {
        throw new Error("If distance is an array of arrays, it must have the same length as polyline array");
      }
      for (let i = 0; i < polyline.length; i++) {
        const segOffsets = (distance as number[][])[i];
        const segCount = (polyline[i].length - 2) / 2;
        if (segOffsets.length !== segCount) {
          throw new Error(
            `Distance array at index ${i} must have length equal to number of segments in polyline (${segCount})`,
          );
        }
      }
    } else {
      if ((distance as number[]).length !== polyline.length) {
        throw new Error("If distance is an array, it must have the same length as polyline array");
      }
    }
  }

  const { dcel, rawOffsetPolygons } = buildOffsetDCELAndPolygon(polyline, distance);

  if (!dcel || rawOffsetPolygons.length === 0) {
    return [];
  }

  const faceWindingNumbers = dcelComputeWindingNumbers(dcel);

  // Union of the faces with positive winding numbers
  const res = setOpByPredicatePolygons(dcel, (idx) => {
    const faceId = dcel.halfEdgeToFace[idx];

    const opp = oppositeHalfEdgeIndex(idx);
    const oppFace = dcel.halfEdgeToFace[opp];

    return faceWindingNumbers[faceId] > 0 && faceWindingNumbers[oppFace] <= 0;
  });

  return res.map(polygonToFaceLoops);
}

/**
 * Computes the winding numbers for each face in a DCEL structure using depth first search.
 *
 * The winding number of a face represents how many times the a curve wraps around a point
 * inside that face. This can be computed by tracing a ray, but it can be done much
 * faster and more robustly by using the DCEL structure.
 *
 * The DFS starts from the exterior face (with winding number 0) and propagates through
 * adjacent faces, incrementing or decrementing the winding number based on the orientation
 * of the crossed half-edge relative to the input curve.
 *
 * @param dcel - The doubly-connected edge list structure representing the polygon
 * @returns An array of winding numbers, one for each face in the DCEL
 */
export function dcelComputeWindingNumbers(dcel: DCEL) {
  if (dcel.faceHalfEdges.length === 0) {
    return [];
  }

  const visited = new Set<number>();
  const windingNumbers = new Array(dcel.faceHalfEdges.length).fill(0);

  const exteriorFace = dcel.faceHalfEdges.findIndex(
    (_, faceIdx) => !dcel.faceIsInterior(faceIdx) && dcel.faceToParentFace[faceIdx] === undefined,
  );

  if (exteriorFace === -1) {
    throw new Error("No exterior face found");
  }

  // Stack for DFS, each entry contains [faceIdx, windingNumber]
  const stack: [number, number][] = [];

  // Helper function to add a face to the stack and set its winding number
  // This is used to avoid adding the same face multiple times to the stack.
  function addFace(faceIdx: number, windingNumber: number) {
    if (visited.has(faceIdx)) {
      return;
    }

    windingNumbers[faceIdx] = windingNumber;
    visited.add(faceIdx);
    stack.push([faceIdx, windingNumber]);
  }

  // start with exterior face
  addFace(exteriorFace, 0);

  while (stack.length > 0) {
    const entry = stack.pop();
    if (!entry) {
      continue;
    }

    const [faceIdx, windingNumber] = entry;

    windingNumbers[faceIdx] = windingNumber;

    const neighborHalfEdges = dcel.faceHalfEdges[faceIdx];

    const childFaces = dcel.faceToChildFaces[faceIdx];

    if (childFaces.length > 0) {
      // Child faces have the same winding number as the parent
      // because they bound the same region
      for (const childFace of childFaces) {
        addFace(childFace, windingNumber);
      }
    }

    for (const halfEdge of neighborHalfEdges) {
      const oppositeHalfEdge = oppositeHalfEdgeIndex(halfEdge);
      const oppositeFace = dcel.halfEdgeToFace[oppositeHalfEdge];

      if (visited.has(oppositeFace)) {
        continue;
      }

      let delta = 0;

      // this correctly handles the case where the input curve doubles back on itself
      delta += dcel.halfEdgeToPolygon[oppositeHalfEdge].includes(0) ? -1 : 1;
      delta += dcel.halfEdgeToPolygon[halfEdge].includes(0) ? 1 : -1;

      addFace(oppositeFace, windingNumber + delta);
    }
  }

  return windingNumbers;
}

/**
 * Generates the DCEL structure and the raw offset polygon from a polyline and offset distance.
 * This is an intermediate step used by offsetPolyLine2.
 *
 * @param polyline The input polyline to offset.
 * @param distance The distance(s) to offset the polyline. Can be a single number,
 * an array of numbers (per polyline), or an array of arrays (per segment per polyline).
 * @returns An object containing the DCEL, the raw offset points. The DCEL is null if the input polygons are degenerate.
 */
export function buildOffsetDCELAndPolygon(
  polyline: PolyLine2[],
  distance: OffsetDistance,
): {
  dcel: DCEL | null;
  rawOffsetPolygons: Point[][];
} {
  const rawOffsetPolygons = buildRawOffsetPolygons(polyline, distance);

  if (rawOffsetPolygons.length === 0) {
    return { dcel: null, rawOffsetPolygons };
  }

  // We disable the polygon self overlap check because the offset curve can have self-overlaps.
  const dcel = dcelFromPolygons({
    polygons: [rawOffsetPolygons],
    snapRoundPrecision: 0.001,
    preventPolygonSelfOverlaps: false,
  });

  return { dcel, rawOffsetPolygons };
}

const EDGE_REVERSAL_EPSILON = 1e-1;
const OFFSET_DIFF_EPSILON = 1e-3;
/**
 * Returns the offset distances for the current and next segment of a polyline,
 * given the offset distance parameter.
 * @param dist The distance(s) to offset the polylines.
 * @param polylineIdx The index of the current polyline.
 * @param segIdx The index of the current segment (even index in the polyline array).
 * @param segCount The number of segments in the polyline.
 * @returns [offsetDistBSeg, offsetDistCSeg]
 */
function getOffsetDistances(
  dist: OffsetDistance,
  polylineIdx: number,
  segIdx: number,
  segCount: number,
): [number, number] {
  if (Array.isArray(dist)) {
    if (Array.isArray(dist[0])) {
      const dists = dist as number[][];
      return [dists[polylineIdx][segIdx % segCount], dists[polylineIdx][(segIdx + 1) % segCount]];
    } else {
      const dists = dist as number[];
      return [dists[polylineIdx], dists[polylineIdx]];
    }
  } else {
    return [dist as number, dist as number];
  }
}

/**
 * Builds raw offset polygons from the polylines and offset distance.
 *
 * See https://mcmains.me.berkeley.edu/pubs/DAC05OffsetPolygon.pdf for a description of what this looks like.
 *
 * @param polygons The input polylines to offset.
 * @param dist The distance(s) to offset the polylines. Can be a single
 * number, an array of numbers (per polyline), or an array of arrays
 * (per segment per polyline).
 * @returns The raw offset polygons suitable to be passed to dcelFromPolygons.
 */
export function buildRawOffsetPolygons(polygons: PolyLine2[], dist: OffsetDistance): Point[][] {
  const rawOffsetPolygons: Point[][] = [];

  for (let i = 0; i < polygons.length; i++) {
    const polygon = polygons[i];
    const offsetPolygon: Point[] = [];
    const l = polygon.length - 2;

    let ax = polygon[l - 2];
    let ay = polygon[l - 1];

    for (let j = 0; j < l; j += 2) {
      const segCount = (polygon.length - 2) / 2;
      const [offsetDistABSeg, offsetDistBCSeg] = getOffsetDistances(dist, i, segCount - 1 + j / 2, segCount);

      const bx = polygon[j];
      const by = polygon[j + 1];
      const cx = polygon[(j + 2) % l];
      const cy = polygon[(j + 3) % l];

      if (vec2Dist(bx, by, cx, cy) < DIST_EPSILON) {
        ax = bx;
        ay = by;
        continue;
      }

      const [tanAX, tanAY] = vec2Tangent(ax, ay, bx, by);
      const [tanBX, tanBY] = vec2Tangent(bx, by, cx, cy);

      // are the edges reversed?
      const edgeDoublesBack = Math.abs(vec2Dot(tanAX, tanAY, tanBX, tanBY) + 1) < EDGE_REVERSAL_EPSILON;
      // are the edges collinear (not reversed)?
      const edgeCollinear = Math.abs(vec2Dot(tanAX, tanAY, tanBX, tanBY) - 1) < EDGE_REVERSAL_EPSILON;
      const offsetsDiffer = Math.abs(offsetDistABSeg - offsetDistBCSeg) > OFFSET_DIFF_EPSILON;

      const [aa, bb] = offsetSegment(ax, ay, bx, by, offsetDistABSeg);
      const [bbb, cc] = offsetSegment(bx, by, cx, cy, offsetDistBCSeg);

      const convex = isConvex(ax, ay, bx, by, cx, cy);
      if ((offsetDistABSeg > 0 && !convex) || (offsetDistABSeg < 0 && convex)) {
        if (!edgeDoublesBack) {
          offsetPolygon.push(bb);
          offsetPolygon.push([bx, by]);
          offsetPolygon.push(bbb);
        } else {
          // create butt cap
          const [nx, ny] = vec2Normal(ax, ay, bx, by);
          const [tx, ty] = vec2Tangent(bx, by, ax, ay);

          const [p1x, p1y] = vec2Add(
            bx,
            by,
            nx * offsetDistABSeg + tx * offsetDistBCSeg,
            ny * offsetDistABSeg + ty * offsetDistBCSeg,
          );
          const [p2x, p2y] = vec2Add(
            bx,
            by,
            -nx * offsetDistABSeg + tx * offsetDistBCSeg,
            -ny * offsetDistABSeg + ty * offsetDistBCSeg,
          );

          offsetPolygon.push([p1x, p1y], [p2x, p2y]);
        }
      } else {
        const isect = intersectLine2Line2(
          aa[0],
          aa[1],
          bb[0],
          bb[1],
          bbb[0],
          bbb[1],
          cc[0],
          cc[1],
          undefined,
          undefined,
          false,
        );

        // parallel edges
        if (!isect) {
          if (edgeDoublesBack) {
            // create butt cap
            const [nx, ny] = vec2Normal(ax, ay, bx, by);
            const [tx, ty] = vec2Tangent(ax, ay, bx, by);

            const [p1x, p1y] = vec2Add(
              bx,
              by,
              nx * offsetDistABSeg + tx * offsetDistBCSeg,
              ny * offsetDistABSeg + ty * offsetDistBCSeg,
            );
            const [p2x, p2y] = vec2Add(
              bx,
              by,
              -nx * offsetDistABSeg + tx * offsetDistBCSeg,
              -ny * offsetDistABSeg + ty * offsetDistBCSeg,
            );

            offsetPolygon.push([p1x, p1y], [p2x, p2y]);
          } else if (edgeCollinear && offsetsDiffer) {
            // For collinear (not reversed) edges with differing offsets, insert both offset endpoints
            offsetPolygon.push(bb);
            offsetPolygon.push(bbb);
          } else {
            offsetPolygon.push(bb);
          }
        }

        if (isect instanceof CurveCurvePointIntersection) {
          offsetPolygon.push([isect.x, isect.y]);
        } else if (isect instanceof CurveCurveRangeIntersection) {
          offsetPolygon.push([isect.startx, isect.starty], [isect.endx, isect.endy]);
        }
      }

      ax = bx;
      ay = by;
    }

    rawOffsetPolygons.push(offsetPolygon);
  }

  return rawOffsetPolygons;
}

function offsetSegment(ax: number, ay: number, bx: number, by: number, distance: number): [Point, Point] {
  const [nx, ny] = vec2Normal(ax, ay, bx, by);

  return [
    [ax + nx * distance, ay + ny * distance],
    [bx + nx * distance, by + ny * distance],
  ];
}

const CONVEX_EPSILON = 1e-3;

function isConvex(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): boolean {
  const [tax, tay] = vec2Sub(ax, ay, bx, by);
  const [tbx, tby] = vec2Sub(cx, cy, bx, by);

  return vec2Cross(tax, tay, tbx, tby) > -CONVEX_EPSILON;
}
